import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Edit3,
  User,
  Building2,
  Mail,
  Phone,
  Shield,
  FileCode
} from 'lucide-react';
import { modificationApi } from '../../services/iso20022Api';
import {
  formatDate,
  copyToClipboard,
  downloadFile
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const ModificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [modification, setModification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);

  useEffect(() => {
    loadModification();
  }, [id]);

  const loadModification = async () => {
    try {
      setLoading(true);
      const data = await modificationApi.getModificationById(id);
      setModification(data);
    } catch (err) {
      console.error('Failed to load modification:', err);
      setError('Modification not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (field, value) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownloadXml = () => {
    if (modification?.rawXml) {
      const fileName = `modification-acmt003-${modification.id}.xml`;
      downloadFile(modification.rawXml, fileName, 'application/xml');
    }
  };

  const handleViewXml = () => {
    setXmlModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading modification details...</p>
        </div>
      </div>
    );
  }

  if (error || !modification) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Modification Not Found</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/iso20022/modifications')} className="btn-primary">
            Back to Modifications
          </button>
        </div>
      </div>
    );
  }

  const InfoRow = ({ label, value, copyable = false, fieldName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center justify-between">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 font-mono flex items-center space-x-2">
          <span>{value || 'N/A'}</span>
          {copyable && value && (
            <button
              onClick={() => handleCopy(fieldName, value)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === fieldName ? (
                <CheckCircle className="w-4 h-4 text-success-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
        </dd>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusClasses = {
      'COMPLETED': 'bg-success-100 text-success-800',
      'SIGNED': 'bg-success-100 text-success-800',
      'PROCESSING': 'bg-warning-100 text-warning-800',
      'RECEIVED': 'bg-info-100 text-info-800',
      'SENT': 'bg-info-100 text-info-800',
      'REJECTED': 'bg-danger-100 text-danger-800'
    };

    const badgeClass = statusClasses[status?.toUpperCase()] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  const getModificationScopeBadge = (scope) => {
    const scopeClasses = {
      'ADD': 'bg-green-100 text-green-800',
      'UPDT': 'bg-blue-100 text-blue-800',
      'DEL': 'bg-red-100 text-red-800'
    };

    const scopeLabels = {
      'ADD': 'Addition',
      'UPDT': 'Update',
      'DEL': 'Deletion'
    };

    const badgeClass = scopeClasses[scope] || 'bg-gray-100 text-gray-800';
    const label = scopeLabels[scope] || scope;
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/modifications')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Modification #{modification.id}
            </h1>
            <p className="text-gray-600 mt-1">Account modification request (acmt.003)</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {modification.rawXml && (
            <>
              <button
                onClick={handleViewXml}
                className="btn-secondary flex items-center space-x-2"
              >
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
              <button
                onClick={handleDownloadXml}
                className="btn-secondary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </>
          )}
          <button
            onClick={loadModification}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Edit3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="mt-1">
                  {getStatusBadge(modification.status)}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-lg ${
                modification.signatureOk 
                  ? 'bg-success-100' 
                  : 'bg-danger-100'
              }`}>
                {modification.signatureOk ? (
                  <CheckCircle className="w-6 h-6 text-success-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-danger-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600">Signature</p>
                <p className="text-sm font-medium text-gray-900">
                  {modification.signatureOk ? 'Valid' : 'Invalid/Missing'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(modification.createdAt, 'PPp')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Shield className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Direction</p>
                <p className="text-sm font-medium text-gray-900">
                  {modification.direction || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <FileCode className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Message Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-0">
              <InfoRow
                label="Message ID"
                value={modification.msgId}
                copyable={true}
                fieldName="msgId"
              />
              <InfoRow
                label="Creation Date/Time"
                value={modification.creationDateTime}
                copyable={true}
                fieldName="creationDateTime"
              />
              <InfoRow
                label="Message Type"
                value={modification.msgType || 'acmt.003'}
              />
              <InfoRow
                label="Modification Scope"
                value={
                  modification.modificationScope && (
                    <span className="inline-flex">
                      {getModificationScopeBadge(modification.modificationScope)}
                    </span>
                  )
                }
              />
            </dl>
          </div>
        </div>

        {/* Account Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-0">
              <InfoRow
                label="Account ID"
                value={modification.accountId}
                copyable={true}
                fieldName="accountId"
              />
              <InfoRow
                label="Owner Name"
                value={modification.ownerName}
                copyable={true}
                fieldName="ownerName"
              />
              <InfoRow
                label="Modification Reason"
                value={modification.modificationReason}
              />
              <InfoRow
                label="Merchant Code"
                value={modification.merchantCode}
                copyable={true}
                fieldName="merchantCode"
              />
            </dl>
          </div>
        </div>

        {/* Institution Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Institution Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-0">
              <InfoRow
                label="Sender DFSP"
                value={modification.senderDfsp}
                copyable={true}
                fieldName="senderDfsp"
              />
              <InfoRow
                label="Sender Bank Code"
                value={modification.senderBankCode}
                copyable={true}
                fieldName="senderBankCode"
              />
              <InfoRow
                label="Business License ID"
                value={modification.businessLicenseId}
                copyable={true}
                fieldName="businessLicenseId"
              />
              <InfoRow
                label="Business License Type"
                value={modification.businessLicenseType}
              />
            </dl>
          </div>
        </div>

        {/* Contact Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-0">
              <InfoRow
                label="Email"
                value={modification.email}
                copyable={true}
                fieldName="email"
              />
              <InfoRow
                label="Mobile Number"
                value={modification.mobileNumber}
                copyable={true}
                fieldName="mobileNumber"
              />
              <InfoRow
                label="KYC Database Check"
                value={
                  modification.kycDatabaseCheck !== null && modification.kycDatabaseCheck !== undefined ? (
                    modification.kycDatabaseCheck ? (
                      <span className="inline-flex items-center space-x-1 text-success-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>Passed</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1 text-danger-600">
                        <XCircle className="w-4 h-4" />
                        <span>Failed</span>
                      </span>
                    )
                  ) : 'N/A'
                }
              />
            </dl>
          </div>
        </div>
      </div>

      {/* Signature Details */}
      {modification.signatureDetails && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Signature Details</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-0">
              <InfoRow
                label="Signature Method"
                value={modification.signatureMethod}
              />
              <InfoRow
                label="Digest Method"
                value={modification.digestMethod}
              />
              <InfoRow
                label="Canonicalization Method"
                value={modification.canonicalizationMethod}
              />
              {modification.signatureDetails && !modification.signatureOk && (
                <div className="py-3">
                  <dt className="text-sm font-medium text-gray-500 mb-2">Error Details</dt>
                  <dd className="text-sm text-danger-600 bg-danger-50 p-3 rounded-lg font-mono">
                    {modification.signatureDetails}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* XML Viewer Modal */}
      {modification.rawXml && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => setXmlModalOpen(false)}
          xmlContent={modification.rawXml}
          fileName={`modification-acmt003-${modification.id}.xml`}
          title={`Account Modification #${modification.id}`}
        />
      )}
    </div>
  );
};

export default ModificationDetail;
